import { ExpandMore, ShoppingBasket, Widgets, WidgetsOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Divider, Grid, IconButton, InputAdornment, LinearProgress, List, ListItem, ListItemButton, Paper, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import Api from '../../Api';
import { BasketContext } from '../../contexts/BasketContext';
import { Feature, PermissionContext } from '../../contexts/PermissionContext';
import { ReturnContext } from '../../contexts/ReturnContext';
import { IAddToBasketDialogProps, IBasketItem, IMaterial, IMaterialGroup, MaterialContext } from '../../interfaces';
import { UID } from '../../utils';
import { MaterialInputDialog } from '../MaterialInputDialog';
import { QuantityPicker } from '../Shared/FormControls/QuantityPicker';
import { StoredAssemblyComponent } from '../Shared/StoredAssemblyComponent';


export interface IStoredAssembly {
    material: IMaterial,
    components: IMaterial[]
}

export const AddToBasketDialog: React.SFC<IAddToBasketDialogProps> = ({
    materialGroup,
    onSubmitMaterial
}: IAddToBasketDialogProps) => {

    const [group, setGroup] = useState<IMaterialGroup>();

    const basketContext = useContext(BasketContext);
    const returnContext = useContext(ReturnContext);
    const permissionContext = useContext(PermissionContext);

    const [available, setAvailable] = useState<number>(0);


    useEffect(() => {
        let total = group?.materials
            ?.map(x => x.quantity ?? 0)
            .reduce((sum, a) => sum + a, 0) ?? 0;
        if (total !== available)
            setAvailable(total);

    }, [group, available])



    let isReturn = materialGroup.status === 3;
    let isReturned = materialGroup.totalAvailable === 0;
    let isStrategic = ['STRATEGIC', 'TOOLING'].includes(materialGroup.spareType?.toUpperCase() ?? "")
    let isAllowed = permissionContext.isPermitted(Feature.Dispatch_ContractSpares) ? true : isStrategic;


    const onSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {

        e.preventDefault();

        if (!(qty > 0 && qty <= available))
            alert("invalid quantity");

        let targetContext = (isReturn === true) ? returnContext : basketContext;

        let basket = cloneDeep(targetContext.basket);

        //remove previous instances of this group from the basket
        basket.items = basket.items.filter(x => x.materialGroup.id !== group?.id);
        if (group) {
            let item: IBasketItem = {
                id: UID.generate(),
                materialGroup: group,
                requestedQuantity: qty
            };

            basket.items.push(item);
        }
        targetContext.setBasket(basket);

        onSubmitMaterial(qty);
        setAdded(true);
        setOpen(false);
    }

    const onCancel = () => {

        setOpen(false);
    }

    const onSwitchTo = (materialId?: string) => {
        //a material has been selected from the stored assembly
        //so now we want to replace the stored assembly with that
        //component material to add it to the basket instead.

        setGroup(undefined);

        if (materialId)
            Api.Materials.get(materialId).then((response) => {

                let fullMaterial = response as IMaterial;

                let newGroup: IMaterialGroup = {
                    catalogueNumber: fullMaterial.catalogueNumber,
                    contractReferenceNumber: fullMaterial.contractReference,
                    classes: fullMaterial.classes,
                    id: fullMaterial.materialId,
                    ownedBy: fullMaterial.ownedBy ?? "",
                    partNumber: fullMaterial.partNumber,
                    restricted: fullMaterial.hasUsageRestrictions,
                    site: fullMaterial.site?.value,
                    spareType: fullMaterial.spareType?.value,
                    state: fullMaterial.state?.value,
                    title: fullMaterial.l3,
                    subtitle: fullMaterial.l1?.value,
                    subtitle2: fullMaterial.l2?.value,
                    totalAvailable: fullMaterial.quantity,
                    materials: [fullMaterial]
                }

                setQty(1);
                setGroup(newGroup);
            })
    }

    const onAddMaterial = (material: IMaterial, storedAssembly: IMaterial) => {
        setGroup(undefined);
        setAssemblyComponents([]);

        material.storedAssemblyId = storedAssembly.materialId;

        Api.Materials.create(material).then(response => {
            onSwitchTo((response as IMaterial).materialId);
        });
    }

    const isInBasket = () => {

        let targetContext = isReturn ? returnContext : basketContext;

        targetContext.basket.items.forEach(item => {
            if (item.materialGroup.id === group?.id)
                return true;
        });

        return false;
    }

    const [open, setOpen] = useState(false);
    const [qty, setQty] = useState(1);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [added, setAdded] = useState(isInBasket());
    const [assemblyComponents, setAssemblyComponents] = useState<IStoredAssembly[]>([]);



    const getBasketButtonLabel = () => {

        if (isReturn)
            if (isReturned)
                return "Returned"
            else
                return added ? 'Returning' : 'Return';
        else
            return added ? 'Added' : 'Add';
    }

    useEffect(() => { }, [basketContext.basket.items, returnContext.basket.items, assemblyComponents])

    useEffect(() => {
        if (group !== undefined) {

            let components: IStoredAssembly[] = [];

            const fetchMaterials = async () => {
                await Promise.all(
                    (group.materials ?? []).map(async (assembly) => {
                        let assemblyId = assembly.materialId ?? "";
                        if (assemblyId) {
                            try {

                                let componentSet: IStoredAssembly = {
                                    material: assembly,
                                    components: []
                                };

                                const response = await Api.Materials.StoredAssembly.get(assemblyId);
                                const materials = response as IMaterial[];
                                materials.forEach((material) => {
                                    componentSet.components.push(material);
                                });
                                components.push(componentSet);
                            } catch (error) {
                                // Handle error if needed
                            }
                        }
                    })
                );
                setAssemblyComponents(components);
            };
            fetchMaterials();
        }
    }, [group]);

    return (
        <span onClick={(e) => { e.stopPropagation(); }}>

            <Tooltip title={"Add to basket"}>
                <Button
                    variant="contained"
                    color={isReturn ? "warning" : "primary"}
                    onClick={() => { setOpen(true); setGroup(materialGroup) }}
                    endIcon={<ShoppingBasket />}
                    disabled={!isAllowed}
                >
                    {getBasketButtonLabel()}
                </Button>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                {group ?
                    <form
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <DialogTitle>
                            Add to {isReturn ? "Returns" : "Basket"}
                        </DialogTitle>
                        <DialogContent>

                            <Alert icon={false} color="info" elevation={0} sx={{ width: "100%" }} >
                                <Typography variant="body1">{group?.title}</Typography>
                                <Typography variant="body2">{group?.subtitle}</Typography>
                                <Typography variant="body2">{group?.subtitle2}</Typography>
                                <Grid item={true} alignItems="center">
                                    {group.materials?.length === 1 && group.materials[0].storedAssemblyId &&
                                        <>
                                            <span><small><i>stored assembly: </i></small></span>
                                            <Chip
                                                size="small"
                                                icon={<Widgets />}
                                                label={group.materials[0].storedAssembly}
                                                variant="outlined"
                                                onClick={() => onSwitchTo(group.materials ? group.materials[0].storedAssemblyId : undefined)}
                                            />
                                        </>

                                    }
                                </Grid>
                            </Alert>
                            <Grid container sx={{ mt: 3 }}>
                                <Grid item xs={6} sx={{ p: 0.5 }}>
                                    <QuantityPicker
                                        action={isReturn ? "Returning" : "Required"}
                                        available={available}
                                        value={qty}
                                        onChange={setQty}
                                    />
                                </Grid>

                                {!isReturn &&
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Accordion
                                            defaultExpanded={false}
                                            elevation={assemblyComponents.length > 0 ? 1 : 0}
                                            variant="outlined"
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}


                                            >
                                                <DialogContentText fontSize="small" color={assemblyComponents.length > 0 ? "primary" : "lightgrey"}>
                                                    <strong>Stored Assembly Options</strong>
                                                </DialogContentText>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="caption">You can add a material to the basket that is part of a stored assembly by selecting it below.</Typography>

                                                {assemblyComponents.length > 0 && assemblyComponents.map((componentSet) => {
                                                    return (
                                                        <Paper elevation={0} sx={{ mt: 2 }} style={{ maxHeight: 200, width: '100%', overflow: 'auto', border: '1px solid lightgrey' }}>
                                                            <List>
                                                                <StoredAssemblyComponent material={componentSet.material} />
                                                                <Divider />
                                                                {
                                                                    componentSet.components.length > 0 ?
                                                                        componentSet.components.map(component => {
                                                                            return (
                                                                                <ListItemButton onClick={() => onSwitchTo(component.materialId)}>
                                                                                    <WidgetsOutlined sx={{ mr: 1 }} />    {component.l3}
                                                                                </ListItemButton>
                                                                            );
                                                                        })
                                                                        :

                                                                        <ListItem>
                                                                            <Typography variant="subtitle2">There are currently no materials associated with this stored assembly.</Typography>
                                                                        </ListItem>
                                                                }
                                                                <Divider />
                                                                <ListItem>
                                                                    <MaterialInputDialog
                                                                        focus={false}
                                                                        context={MaterialContext.StoredAssembly}
                                                                        entity={{ id: group?.id ?? "", type: "BASKET" }}
                                                                        onSubmitMaterial={(material: IMaterial) => { onAddMaterial(material, componentSet.material) }}
                                                                    />
                                                                    <span>
                                                                        <Typography sx={{ ml: 1 }} variant="subtitle2">to this stored assembly</Typography>
                                                                    </span>
                                                                </ListItem>
                                                            </List>
                                                        </Paper>)
                                                })}
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                }

                            </Grid>

                            {errorMessage &&
                                <DialogContentText color="error">
                                    {errorMessage}
                                </DialogContentText>
                            }
                        </DialogContent>
                        <DialogActions>

                            <Button onClick={onCancel} color="warning">Cancel</Button>

                            <Button
                                type="button"
                                onClick={(e) => onSubmit(e)}
                                disabled={qty < 1}
                            >Add To {isReturn ? "Returns" : "Basket"}</Button>

                        </DialogActions>

                    </form> : <DialogContent><LinearProgress /></DialogContent>
                }
            </Dialog>
        </span>
    );
}